import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { get, noop } from "lodash";
import { Form } from "@unform/web";
import { array } from "yup";
import { useLocation } from "react-router-dom";
import tvgConf from "@tvg/conf";

import RegularPageRegistration from "@tvg/atomic-ui/_templates/RegularPageRegistration";
import Button from "@tvg/atomic-ui/_atom/Buttons/button";
import { eyeLineShow, eyeLineHide } from "@tvg/atomic-ui/_static/Icons/icons";
import buildColor from "@tvg/atomic-ui/_static/ColorPalette";
import { fontMedium, fontNormal } from "@tvg/atomic-ui/_static/Typography";
import Icon from "@tvg/atomic-ui/_static/Icons";
import InputSSN from "@tvg/atomic-ui/_molecule/InputSSN";
import {
  PerimeterxContainer,
  RobotMessage
} from "@tvg/atomic-ui/_templates/Login/styled-components";
import { SubmitButton } from "@tvg/atomic-ui/_templates/RegularPageRegistration/styled-components";
import {
  handleInputOnTheFlyChange,
  handleFullSSNValidation,
  handleInputOnBlurGtm,
  onClickCustomerServiceButton
} from "../utils/handlers";
import { onVirtualPageViewGtmEvent } from "../utils/gtm/pageViews";

import {
  getAddressFields,
  getCurrentStep,
  getFullSsnIsFormValid,
  getFullSsnToggle,
  getOnfidoToggle,
  getPerimeterXToggle,
  getRegistrationTextsPage,
  getRobotMessageToggle,
  getSelectedPromo,
  getTvProvider,
  getWelcomeFields
} from "../utils/selectors";

export const fullSSNSchema = (fullSsnTexts) =>
  array()
    .test("check-empty", fullSsnTexts.errors.empty, (ssn) => ssn.join(""))
    .test(
      "check-fulfilled",
      fullSsnTexts.errors.incomplete,
      (ssn) => ssn.join("").length === 9
    )
    .test(
      "check-invalid-sequence",
      fullSsnTexts.errors.invalidSequence,
      (ssn) =>
        /^(?!(000|666|9))\d{3}-?(?!(00))\d{2}-?(?!(0000))\d{4}$/.test(
          ssn.join("")
        )
    );

const FullSsnComponent = ({ device }) => {
  const location = useLocation();
  const dispatch = useDispatch();

  const enableRegistrationPerimeterX = useSelector(getPerimeterXToggle);
  const enableOnfido = useSelector(getOnfidoToggle);
  const regFullSSNCS = useSelector(getFullSsnToggle);

  const robotMessage = useSelector(getRobotMessageToggle);
  const fullSsnTexts = useSelector(getRegistrationTextsPage("fullSsn"));
  const currentStep = useSelector(getCurrentStep);
  const isFormValid = useSelector(getFullSsnIsFormValid);
  const selectedPromo = useSelector(getSelectedPromo);
  const welcomeFields = useSelector(getWelcomeFields);
  const addressFields = useSelector(getAddressFields);
  const selectedTvProvider = useSelector(getTvProvider);

  const formRef = useRef(null);

  const [isHumanChallengeEnabled, setIsHumanChallengeEnabled] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSSNVisible, setIsSSNVisible] = useState(false);

  const onSubmit = handleFullSSNValidation({
    fullSsnTexts,
    welcomeFields,
    addressFields,
    selectedPromo,
    selectedTvProvider,
    setIsLoading,
    setIsFormDirty,
    formRef,
    dispatch,
    isFormValid,
    ssnSchema: fullSSNSchema(fullSsnTexts),
    setIsHumanChallengeEnabled,
    enableRegistrationPerimeterX,
    enableOnfido
  });

  useEffect(() => {
    onVirtualPageViewGtmEvent(location, "fullssn-details");
  }, []);

  useEffect(() => {
    const handler = async () => {
      await onSubmit(formRef.current?.getData());
      setIsHumanChallengeEnabled(false);
    };

    if (typeof window !== "undefined") {
      window.addEventListener("passedHC", handler);
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("passedHC", handler);
      }
    };
  }, [formRef.current, onSubmit]);

  return (
    <RegularPageRegistration
      device={device}
      brand={tvgConf().brand}
      innerTitle={get(fullSsnTexts, "innerTitle", "")}
      infoTitle={get(fullSsnTexts, "innerDescriptionTitle", "")}
      infoText={[
        {
          status: "COMMON",
          text: fullSsnTexts.innerDescription[0],
          color: buildColor("grey", "200")
        },
        {
          status: "COMMON",
          text: fullSsnTexts.innerDescription[1],
          color: buildColor("grey", "200")
        },
        {
          status: "COMMON",
          text: fullSsnTexts.innerDescription[2],
          color: buildColor("grey", "200")
        },
        {
          status: "COMMON",
          text: fullSsnTexts.innerDescription[3],
          color: buildColor("grey", "200")
        }
      ]}
      handleBackButton={noop}
      currentStep={currentStep}
      msgBoxTitle={fullSsnTexts.msgBoxTitle}
      isFullSsn
    >
      <Form ref={formRef} onSubmit={onSubmit}>
        <InputSSN
          name="ssn"
          label={fullSsnTexts.inputLabel}
          isVisible={isSSNVisible}
          icon={
            <Icon
              icon={isSSNVisible ? eyeLineHide : eyeLineShow}
              size={16}
              viewBoxSize={16}
              color={buildColor("blue_accent", "500")}
            />
          }
          handleIconClick={() => setIsSSNVisible(!isSSNVisible)}
          customHandleOnBlur={handleInputOnBlurGtm(
            "fullSsn",
            fullSSNSchema(fullSsnTexts),
            false,
            fullSsnTexts.innerTitle
          )}
          customHandleChange={(value) => {
            handleInputOnTheFlyChange(
              isFormDirty,
              "ssn",
              value,
              fullSSNSchema(fullSsnTexts),
              formRef,
              fullSsnTexts.innerTitle,
              false
            );
          }}
          align="center"
          size="large"
          marginTop={12}
          marginBottom={24}
          borderTop={1}
          paddingTop={12}
          isFullSsn
          sectionTitle={fullSsnTexts.inputSectionTitle}
          autoFocus
        />
        {isHumanChallengeEnabled && <RobotMessage>{robotMessage}</RobotMessage>}
        <PerimeterxContainer id="pxElement" />
        {!isHumanChallengeEnabled && (
          <SubmitButton
            size="bigger"
            hasRoundedCorners
            isStretched
            isUppercase={false}
            hasShadow
            fontFamily={fontNormal}
            qaLabel="registration-submit-btn"
            isLoading={isLoading}
            showDisabled={isLoading}
            loadingBackgroundColor={buildColor("white", "100")}
            loadingBorderColor={buildColor("white", "30")}
            customMarginBottom={16}
          >
            Get verified
          </SubmitButton>
        )}
      </Form>
      {!isHumanChallengeEnabled && regFullSSNCS && (
        <Button
          size="bigger"
          hasRoundedCorners
          isStretched
          isUppercase={false}
          hasShadow
          type="secondary"
          fontFamily={fontMedium}
          isBold={false}
          qaLabel="registration-chat-with-agent-btn"
          onClick={onClickCustomerServiceButton}
        >
          Chat with a TVG agent
        </Button>
      )}
    </RegularPageRegistration>
  );
};

export default FullSsnComponent;
